import Link from 'next/link'
import { btn, cx, input, label, notice } from '@/lib/ui'
import { Wordmark } from '@/components/workspace/Logo'

export function AuthShell({
  title,
  intro,
  children,
  footer,
}: {
  title: string
  intro?: string
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <div className="flex flex-1 items-center justify-center px-4 py-12 sm:py-16">
      <div className="w-full max-w-[26rem]">
        <div className="glass rounded-[1.75rem] p-7 sm:p-8">
          <Wordmark className="mx-auto block h-9 w-auto text-[var(--ink)]" />
          <h1 className="mt-5 text-2xl font-semibold tracking-tight">{title}</h1>
          {intro && <p className="mt-1.5 text-sm leading-relaxed text-muted">{intro}</p>}
          {children}
        </div>
        {footer && <div className="mt-5 text-center text-sm text-muted">{footer}</div>}
      </div>
    </div>
  )
}

export function Field({
  id,
  name,
  type = 'text',
  labelText,
  autoComplete,
  placeholder,
  defaultValue,
  required = true,
  minLength,
  autoFocus,
}: {
  id: string
  name: string
  type?: string
  labelText: string
  autoComplete?: string
  placeholder?: string
  defaultValue?: string
  required?: boolean
  minLength?: number
  autoFocus?: boolean
}) {
  return (
    <div className="mt-4">
      <label htmlFor={id} className={label}>
        {labelText}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        defaultValue={defaultValue}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
        className={cx(input, 'mt-1.5 py-2.5')}
      />
    </div>
  )
}

export function Notice({
  tone,
  children,
}: {
  tone: 'error' | 'success' | 'info'
  children: React.ReactNode
}) {
  return (
    <p role={tone === 'error' ? 'alert' : 'status'} className={cx('mt-4', notice[tone])}>
      {children}
    </p>
  )
}

export function SubmitButton({ children }: { children: React.ReactNode }) {
  return (
    <button type="submit" className={cx(btn.base, btn.primary, 'mt-6 w-full py-2.5')}>
      {children}
    </button>
  )
}

export function AuthLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="font-medium text-foreground underline decoration-[var(--border-strong)] underline-offset-4 transition-colors hover:decoration-foreground"
    >
      {children}
    </Link>
  )
}
